/**
 * SEO Utility Functions
 * Provides comprehensive SEO meta tags and structured data for pages
 */

/**
 * Set comprehensive SEO meta tags for a page
 * @param {Object} options - SEO configuration options
 * @param {string} options.title - Page title
 * @param {string} options.description - Meta description
 * @param {string} options.url - Canonical URL
 * @param {string} [options.image] - Open Graph image URL
 * @param {string} [options.type] - Open Graph type (default: 'website')
 * @param {string} [options.keywords] - Meta keywords (optional)
 */
export const setSEOMetaTags = ({
  title,
  description,
  url,
  image = 'https://prosperiacrm.com/icons/logo_1024x1024-noBGm.webp',
  type = 'website',
  keywords = ''
}) => {
  // Set document title
  document.title = title

  // Set or update meta description
  let metaDescription = document.querySelector('meta[name="description"]')
  if (!metaDescription) {
    metaDescription = document.createElement('meta')
    metaDescription.setAttribute('name', 'description')
    document.head.appendChild(metaDescription)
  }
  metaDescription.setAttribute('content', description)

  // Set robots meta tag (index, follow)
  let metaRobots = document.querySelector('meta[name="robots"]')
  if (!metaRobots) {
    metaRobots = document.createElement('meta')
    metaRobots.setAttribute('name', 'robots')
    document.head.appendChild(metaRobots)
  }
  metaRobots.setAttribute('content', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1')

  // Set canonical URL
  let canonical = document.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', url)

  // Set keywords if provided
  if (keywords) {
    let metaKeywords = document.querySelector('meta[name="keywords"]')
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta')
      metaKeywords.setAttribute('name', 'keywords')
      document.head.appendChild(metaKeywords)
    }
    metaKeywords.setAttribute('content', keywords)
  }

  // Open Graph tags
  const setOGTag = (property, content) => {
    let tag = document.querySelector(`meta[property="${property}"]`)
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute('property', property)
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  setOGTag('og:title', title)
  setOGTag('og:description', description)
  setOGTag('og:url', url)
  setOGTag('og:image', image)
  setOGTag('og:type', type)
  setOGTag('og:site_name', 'Prosperia CRM')
  setOGTag('og:locale', 'en_US')

  // Twitter Card tags
  const setTwitterTag = (name, content) => {
    let tag = document.querySelector(`meta[name="${name}"]`)
    if (!tag) {
      tag = document.createElement('meta')
      tag.setAttribute('name', name)
      document.head.appendChild(tag)
    }
    tag.setAttribute('content', content)
  }

  setTwitterTag('twitter:card', 'summary_large_image')
  setTwitterTag('twitter:title', title)
  setTwitterTag('twitter:description', description)
  setTwitterTag('twitter:image', image)
  setTwitterTag('twitter:site', '@prosperiacrm')
  setTwitterTag('twitter:creator', '@prosperiacrm')
}

/**
 * Add structured data (JSON-LD) for a feature page
 * @param {Object} options - Structured data options
 * @param {string} options.name - Feature name
 * @param {string} options.description - Feature description
 * @param {string} options.url - Feature page URL
 * @param {string} [options.image] - Feature image URL
 */
export const addFeatureStructuredData = ({
  name,
  description,
  url,
  image = 'https://prosperiacrm.com/icons/logo_1024x1024-noBGm.webp'
}) => {
  // Remove existing structured data for this feature if it exists
  const existingScript = document.querySelector(`script[data-feature-seo="${name}"]`)
  if (existingScript) {
    existingScript.remove()
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `${name} - Prosperia CRM`,
    applicationCategory: 'BusinessApplication',
    description: description,
    url: url,
    image: image,
    provider: {
      '@type': 'Organization',
      name: 'Prosperia CRM',
      url: 'https://prosperiacrm.com'
    },
    offers: {
      '@type': 'Offer',
      price: '39.99',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '10000'
    }
  }

  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.setAttribute('data-feature-seo', name)
  script.textContent = JSON.stringify(structuredData)
  document.head.appendChild(script)
}

/**
 * Complete SEO setup for a feature page
 * @param {Object} options - SEO configuration
 */
export const setupFeaturePageSEO = (options) => {
  setSEOMetaTags(options)
  addFeatureStructuredData({
    name: options.title.replace(' - Prosperia CRM', ''),
    description: options.description,
    url: options.url,
    image: options.image
  })
}

